// Vue core libraries - Pinia, VueUse & Vue
import { acceptHMRUpdate, defineStore } from "pinia";
import { computed, ref, shallowRef } from "vue";

// TMI - content type and query definitions
import type { Category } from "@tmi/types/entities";
import { refreshCategoriesFromServer } from "@/services/data/sync/category-sync-coordinator";
import { cache, CacheClosedError, categories } from "@/services/cache";
import { createInitLatch } from "@/stores/init-latch";
import { createCatalogIndex, markRawRows } from "@/stores/catalog-index";

// Non-reactive variables
import { createDefaultLogger } from "@/types/console-logger";
const logger = createDefaultLogger();

// State values
const initialized = computed< boolean >( () => allCategories.value.length > 0 );
const loading = ref< boolean >( false );
const refreshing = ref< boolean >( false );
// tmi-7be: a cache failure stops initialization until reset(), so no watcher can re-run it in a loop.
const initLatch = createInitLatch();

// Rank 11: a read-only catalog of raw rows, always replaced by assignment; hydration resolves each item's categories.
const allCategories = shallowRef< Category[] >( [] );
const categoriesById = createCatalogIndex( allCategories, ( category: Category ) => category.id );
const categoriesBySlug = createCatalogIndex( allCategories, ( category: Category ) => category.slug );

export const useCategoriesStore = defineStore( 'categories', () => {
    function reset(): void {
        allCategories.value = [];
        categoriesById.clear();
        categoriesBySlug.clear();
        loading.value = false;
        refreshing.value = false;
        initLatch.clear();
    }

    async function initialize(): Promise< void > {
        if ( initialized.value || loading.value || initLatch.isHalted() ) {
            logger.debug( '[ Categories ].initialize() exiting - already processed...' );
            return;
        }

        logger.debug( `[ Categories ] Checking the local database for any categories...` );

        loading.value = true;

        const t0: number = performance.now();

        // The cache read is wrapped so a rejected open (CacheClosedError — a newer build upgraded the
        // database and this tab is reloading) stops quietly instead of escaping initialize() as an
        // unhandled rejection. The `else` branch keeps its own fire-and-forget settlement, so there is
        // deliberately no outer `finally` clearing the flags out from under it.
        try {
            // Wait for the database to open before the first read.
            await cache.ready();

            const uniqueCategories: Map< string, Category > = new Map< string, Category >();
            if ( !await categories.isEmpty() ) {
                try {
                    for ( const category of markRawRows( await categories.all() ) ) {
                        uniqueCategories.set( category.id, category );
                    }
                    allCategories.value = [ ...( Array.from( uniqueCategories.values() ) ) ];

                    const t1: number = performance.now();
                    logger.info( `(local) Loaded ${ allCategories.value.length } categories in ${ t1 - t0 } ms` );
                } finally {
                    // Clear the "loading" flag
                    loading.value = false;
                    refreshing.value = false;
                }
            } else {
                if ( refreshing.value ) {
                    return;
                }

                refreshing.value = true;

                // Timing - testing to see how expensive each method is
                const t0: number = performance.now();

                refreshCategoriesFromServer()
                    .then( ( receivedCategories: Category[] ) => allCategories.value = markRawRows( [ ...receivedCategories ] ) )
                    .catch( ( err: any ) => {
                        logger.error( JSON.stringify( err.message ) );
                        reset();
                    } )
                    .finally( () => {
                        // Clear the "loading" flag
                        loading.value = false;
                        refreshing.value = false;

                        const t1: number = performance.now();
                        logger.info( `Fetching & mapping ${ allCategories.value.length } category items took ${ t1 - t0 } ms` );
                    } );
            }
        } catch ( err: any ) {
            // A newer build upgraded the cache and this tab is reloading. The failure happened before
            // either branch started, so clearing `loading` here is safe; the latch keeps the store from
            // re-initializing until reset() (tmi-7be). The non-quiet path clears `loading` via reset().
            if ( err instanceof CacheClosedError ) { initLatch.halt( err ); loading.value = false; return; }
            logger.error( JSON.stringify( err.message ) );
            reset();
            initLatch.halt( err );   // after reset(), which clears it: a cache failure stays latched
        }
    }

    function categoryById( sysId: string ) : Category | undefined {
        return categoriesById.get( sysId );
    }

    function categoryBySlug( slug: string ): Category | undefined {
        return categoriesBySlug.get( slug );
    }

    return {
        // ACTIONS
        categoryById,
        categoryBySlug,
        initialize,
        reset,

        // GETTERS
        allCategories
    }
} );

if ( import.meta.hot ) {
    import.meta.hot.accept( acceptHMRUpdate( useCategoriesStore, import.meta.hot ) );
}
